/**
 * The icon set.
 *
 * One file of inline SVG rather than an icon package. The set is small and
 * fixed, every glyph inherits `currentColor`, and nothing is fetched at
 * runtime — which matters most on the family portal, where the first screen a
 * parent sees is often on a phone on a slow connection.
 *
 * Every icon is 24×24 on a 1.6 stroke so they sit together without optical
 * weight differences.
 */

import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>

function Icon({ children, ...props }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  )
}

export const IconHome = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 10.5L12 4l8 6.5V20a1 1 0 01-1 1h-4v-6H9v6H5a1 1 0 01-1-1z" />
  </Icon>
)

export const IconStudents = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 19a6 6 0 0112 0M16.5 6.2a3 3 0 010 5.6M21 19a6 6 0 00-2.5-4.9" />
  </Icon>
)

export const IconAcademics = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4l9 4-9 4-9-4z" />
    <path d="M7 10.2V15c0 1.5 2.2 2.6 5 2.6s5-1.1 5-2.6v-4.8M21 8v5" />
  </Icon>
)

export const IconAttendance = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M4 10h16M8 3v4M16 3v4M9.5 14.5l2 2 3.5-4" />
  </Icon>
)

export const IconGrades = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 20V11M10 20V5M15 20v-6M20 20v-9" strokeWidth={2} />
  </Icon>
)

export const IconScheduling = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M4 10h16M8 3v4M16 3v4" />
    <path d="M8 14h3M8 17h6M14 13.5l1.6 1.6L19 11.5" />
  </Icon>
)

export const IconReports = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </Icon>
)

export const IconMessages = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 6.5L12 13l8.5-6.5" />
  </Icon>
)

/**
 * Transportation: a school bus seen from the front over a dashed route line —
 * school movement on a route, not a car, delivery truck or map pin. Strokes
 * only, legible from 16px.
 */
export const IconTransportation = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="2.5" width="14" height="14" rx="2.5" />
    <path d="M5 9.5h14M8 5.5h8" />
    <circle cx="8.5" cy="13" r="0.9" />
    <circle cx="15.5" cy="13" r="0.9" />
    <path d="M7.5 16.5v2M16.5 16.5v2" />
    <path d="M2 21.5h3M8 21.5h3M14 21.5h3M20 21.5h2" />
  </Icon>
)

/** Work in progress: the sun-like cog the module tiles used before Operations had its own mark. */
export const IconCog = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4L5.3 5.3" />
  </Icon>
)

/**
 * Operations: three connected operational blocks around a small gear —
 * departments coordinating, not any one of them. Deliberately not a wrench,
 * building, truck or cart (Facilities, Schools, Transportation, Purchasing).
 * Strokes only, legible from 16px.
 */
export const IconOperations = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="3" width="6" height="5.5" rx="1.5" />
    <rect x="15.5" y="3" width="6" height="5.5" rx="1.5" />
    <rect x="9" y="16" width="6" height="5.5" rx="1.5" />
    <path d="M8.5 7.6l1.6 1.4M15.5 7.6l-1.6 1.4M12 14.1V16" />
    <circle cx="12" cy="11.6" r="2" />
    <path d="M12 8.9v.7M12 13.6v.5M9.3 11.6h.7M14 11.6h.7" />
  </Icon>
)

/**
 * Facilities: a flat-roofed building with a wrench across its lower corner —
 * the physical plant and the work of keeping it running. Deliberately not a
 * gabled building (Administration), a gear (Operations, Settings), a bus or
 * a cart. Strokes only, legible from 16px.
 */
export const IconFacilities = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 21V4.5a1 1 0 011-1h8.5a1 1 0 011 1v6" />
    <path d="M2 21h9" />
    <path d="M6.5 7.5h1.5M10 7.5h1.5M6.5 11h1.5M10 11h1.5M6.5 14.5h1.5" />
    <path d="M19.26 14.03l.39-1.44a2.5 2.5 0 101.76 1.76l-1.44.39z" />
    <path d="M17.2 16.8l-4.2 4.2" />
  </Icon>
)

/**
 * Assets: a property tag tied to a box — the district's things and the
 * label that says whose they are. Not a document (that is Forms and
 * Records), a cart (Purchasing) or a building. Strokes only, legible from 16px.
 */
export const IconAssets = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.2l7.5 4.1v8.4L12 19.8l-7.5-4.1V7.3z" />
    <path d="M4.5 7.3L12 11.4l7.5-4.1M12 11.4v8.4" />
    <path d="M15.6 1.8l4.6 2.5a.8.8 0 01.4.7v2.3" />
    <circle cx="17.6" cy="3.6" r=".7" />
  </Icon>
)

export const IconAdministration = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 21h18M5 21V10l7-5 7 5v11" />
    <path d="M9 21v-5h6v5M9.5 12h5" />
  </Icon>
)

export const IconAssignments = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <path d="M9 2.8h6v3H9zM9 11h6M9 15h4" />
  </Icon>
)

export const IconClasses = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 5.5A1.5 1.5 0 015.5 4H11v16H5.5A1.5 1.5 0 014 18.5z" />
    <path d="M20 5.5A1.5 1.5 0 0018.5 4H13v16h5.5a1.5 1.5 0 001.5-1.5z" />
  </Icon>
)

export const IconResources = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 7a2 2 0 012-2h4l2 2.5h8a2 2 0 012 2V17a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
  </Icon>
)

export const IconBell = (p: IconProps) => (
  <Icon {...p}>
    <path d="M18 9a6 6 0 10-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
    <path d="M10.3 19a2 2 0 003.4 0" />
  </Icon>
)

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l4.5 4.5" />
  </Icon>
)

export const IconChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9.5 5.5L16 12l-6.5 6.5" />
  </Icon>
)

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 9.5L12 16l6.5-6.5" />
  </Icon>
)

export const IconArrowRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 12h14m-5-5.5L19 12l-5.5 5.5" />
  </Icon>
)

export const IconTrendUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 15l5-5 3.5 3.5L20 7" />
    <path d="M15.5 7H20v4.5" />
  </Icon>
)

export const IconCheckCircle = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12.2l2.4 2.4 4.6-5" />
  </Icon>
)

export const IconAlert = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.5l8.5 15h-17z" />
    <path d="M12 10v4M12 16.8v.2" />
  </Icon>
)

export const IconClock = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
)

export const IconStar = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4l2.4 5 5.6.8-4 3.9 1 5.5-5-2.6-5 2.6 1-5.5-4-3.9 5.6-.8z" />
  </Icon>
)

export const IconUserPlus = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="8" r="3.2" />
    <path d="M3.5 19a6 6 0 0112 0M18 8.5v5M20.5 11h-5" />
  </Icon>
)

export const IconHealth = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="6" width="16" height="14" rx="2" />
    <path d="M9 6V4h6v2M12 10v6M9 13h6" />
  </Icon>
)

export const IconShield = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3l7 3v5.5c0 4.2-2.9 7.9-7 9.5-4.1-1.6-7-5.3-7-9.5V6z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
)

export const IconDocument = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
    <path d="M14 3v5h5" />
  </Icon>
)

export const IconMegaphone = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 10v4a1 1 0 001 1h3l6 4V5L8 9H5a1 1 0 00-1 1z" />
    <path d="M17.5 9.2a4 4 0 010 5.6" />
  </Icon>
)

export const IconLogout = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 7V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2h6a2 2 0 002-2v-2" />
    <path d="M10 12h10m-3.5-3.5L20 12l-3.5 3.5" />
  </Icon>
)

export const IconUser = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0116 0" />
  </Icon>
)

/**
 * Finance.
 *
 * A banknote: one rounded rectangle, one medallion, two end rules. Four
 * marks, and the eye resolves it as money before it resolves it as anything
 * else — which is the whole job of a navigation icon at 16px.
 *
 * It is deliberately not the ledger-page sketch this started as. That drawing
 * was conceptually right and visually wrong: a frame plus a column rule plus
 * three rows plus a trend arrow is five marks competing inside 24px, and the
 * silhouette collided with Attendance and Scheduling, which are also
 * rectangles with lines in them. Nothing else in this set is a wide landscape
 * rectangle with a circle at its centre, so this one is legible by shape
 * alone — before any detail is readable at all.
 *
 * No dollar sign: the medallion is blank, so the mark reads as currency
 * rather than as one currency, and it carries no gear, no bank portico and
 * no badge.
 */
export const IconFinance = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
    <circle cx="12" cy="12" r="2.75" />
    <path d="M6.25 9.75v4.5M17.75 9.75v4.5" />
  </Icon>
)

export const IconSettings = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </Icon>
)

/**
 * Purchasing: a purchase-order sheet — order lines above an approval tick.
 * Institutional procurement, deliberately not a cart or a storefront. Plain
 * rectangle corners (no folded corner) keep it distinct from Reports.
 */
export const IconPurchasing = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M8.5 7.5h7M8.5 11h7M8.5 14.5h3" />
    <path d="M12.75 17.25l1.5 1.5 3-3.25" />
  </Icon>
)
